import { createApp } from 'vue';

// Vue core
import App from './App.vue';
import router from './router';
import { pinia } from '@/stores';

// Amplify (GraphQL / AppSync)
import { configureAmplify } from '@/core/graphql/utils/config.util';

// Sentry
import { initSentry } from '@/core/sentry';

configureAmplify();

import { clearSignUpData, session } from '@/helpers/auth';

// Directives
import VueTippy from '@/lib/vue-tippy';
import { clickOutside } from '@/lib/vue-outside-click';
import { registerAccessRolDirective } from '@/shared/access';
import { registerAccessKycDirective } from '@/shared/access/utils/accessKycDirective.util';

import { LAYOUT_CONTENT_GATE_PROVIDER_KEY } from '@/shared/layouts/composables/useLayoutContentGateProvider';
import { LAYOUT_INIT_HOOKS_PROVIDER_KEY } from '@/shared/layouts/composables/useLayoutInitHooksProvider';
import { createLayoutContentGate } from '@/shared/layouts/utils/layoutContentGate.util';
import { startAppShellSkeleton } from '@/shared/layouts/utils/appShellSkeleton.util';
import { resolveInitialLocation } from '@/shared/layouts/utils/initialRoute.util';

import VueFontAwesome from '@/lib/vue-fontawesome';

// Styles
import './assets/styles/tailwind.css';

// ReCaptchaV3
import VueReCaptcha from '@/lib/vue-recaptcha';

// Yup
import Yup from '@/lib/yup';

Yup();

const app = createApp(App);


if (globalThis.window !== undefined) {
  clearSignUpData();
}

initSentry(app, router);

VueFontAwesome(app);

// tooltip
VueTippy(app);

app.directive('outside-click', clickOutside);

// Carga diferida: los overlays de onboarding no entran al bundle de entrada.
app.provide(
  LAYOUT_CONTENT_GATE_PROVIDER_KEY,
  createLayoutContentGate(
    () => import('@/modules/onboarding/views/OnboardingLayoutGate/index.vue')
  )
);

// Los hooks se cargan con el layout, no en el bundle de entrada.
app.provide(LAYOUT_INIT_HOOKS_PROVIDER_KEY, [
  () =>
    import('@/modules/rfq/composables/useRfqLayoutInitHook').then((module) =>
      module.useRfqLayoutInitHook()
    ),
  () =>
    import('@/modules/comex/composables/useComexLayoutInitHook').then(
      (module) => module.useComexLayoutInitHook()
    ),
]);

declare global {
  interface Window { hj: Function; }
}

// Global Components
app.use(pinia);
app.use(router);

const unmountAppShellSkeleton = startAppShellSkeleton({
  hasSession: Boolean(session()),
  route: resolveInitialLocation(router, router.options.history.location),
  loadMainLayout: () => import('@/shared/layouts/pages/MainLayout/index.vue'),
  container: '#app',
});

router
  .isReady()
  .finally(unmountAppShellSkeleton)
  .then(() => {
    app.mount('#app');
  });

VueReCaptcha(app);

registerAccessRolDirective(app)
registerAccessKycDirective(app)